import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import SignoutHeader from "../subcomponents/SignoutHeader";
import { Colors } from "../Colors";
import { RootState, useAppDispatch } from "../store";
import { ActivePlanOfCare, OmniPatient } from "../types";
import { getWaitlistPatients } from "../slices/PatientSlice";
import { api, convertE164ToReadable } from "../utils/utils";

const AT_RISK_MISS_RATE = 0.3;
const AT_RISK_MIN_SAMPLE = 4;
const AUTH_EXPIRING_DAYS = 30;
const NEVER_OPENED_GRACE_HOURS = 24;

type StatusFilter = "all" | "active" | "discharged" | "at-risk" | "auth-expiring" | "auth-exceeded" | "never-opened";

type SortField = "name" | "completion" | "authExpires" | "lastSent" | "lastOpened";
type SortOrder = "asc" | "desc";

const actualCompletionPct = (plan: ActivePlanOfCare): number | undefined => {
  if (!plan.totalAppointments) return undefined;
  return ((plan.appointmentsOccurred ?? 0) / plan.totalAppointments) * 100;
};

const isAtRisk = (plan: ActivePlanOfCare): boolean => {
  if (plan.discharged) return false;
  const missed = plan.appointmentsMissed ?? 0;
  const occurred = plan.appointmentsOccurred ?? 0;
  const sample = missed + occurred;
  if (sample < AT_RISK_MIN_SAMPLE) return false;
  if (missed / sample < AT_RISK_MISS_RATE) return false;
  // Counter-signal: future visits booked => probably on vacation, not dropping off
  if ((plan.appointmentsScheduled ?? 0) > 0) return false;
  // No remaining visits => plan is essentially complete
  if ((plan.appointmentsUnscheduled ?? 0) === 0) return false;
  return true;
};

const isAuthExpiring = (plan: ActivePlanOfCare): boolean => {
  if (plan.ignoreAuthorization) return false;
  if (!plan.authorizationExpires) return false;
  const days = DateTime.fromISO(plan.authorizationExpires).diffNow("days").days;
  return days > 0 && days <= AUTH_EXPIRING_DAYS;
};

const isAuthExceeded = (plan: ActivePlanOfCare): boolean => {
  if (plan.ignoreAuthorization) return false;
  if (plan.authorizationUsed === undefined || plan.authorizationTotal === undefined) return false;
  return plan.authorizationUsed >= plan.authorizationTotal;
};

const isNeverOpened = (plan: ActivePlanOfCare): boolean => {
  if (plan.lastOpened) return false;
  if (!plan.lastSent) return false;
  const hoursSinceSent = -DateTime.fromISO(plan.lastSent).diffNow("hours").hours;
  return hoursSinceSent >= NEVER_OPENED_GRACE_HOURS;
};

const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

const chipBaseSx = {
  height: 22,
  fontSize: "0.7rem",
  fontWeight: 600,
  borderRadius: "6px",
  "& .MuiChip-icon": { fontSize: "13px", marginLeft: "6px" },
  "& .MuiChip-label": { px: 1 },
};

const statusChip = (label: string, variant: "success" | "warning" | "error" | "info", Icon?: React.ElementType) => {
  const palette = {
    success: { bg: "rgba(0,172,79,0.15)", color: Colors.success },
    warning: { bg: "rgba(252,213,84,0.15)", color: Colors.warning },
    error: { bg: "rgba(238,38,38,0.15)", color: Colors.error },
    info: { bg: "rgba(159,161,166,0.15)", color: Colors.info },
  }[variant];
  return (
    <Chip
      key={label}
      size="small"
      label={label}
      icon={Icon ? <Icon sx={{ color: `${palette.color} !important` }} /> : undefined}
      sx={{
        ...chipBaseSx,
        backgroundColor: palette.bg,
        color: palette.color,
        mr: 0.5,
        mb: 0.5,
      }}
    />
  );
};

export const PlanOfCareDashboardPage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const patientMap = useSelector((state: RootState) => state.patients.patientMap);
  const patients = useSelector((state: RootState) => state.patients.patients);
  const providerGroups = useSelector((state: RootState) => state.providers.providerGroups);
  const webptProviders = useSelector((state: RootState) => state.webptIntegration.providers);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const providerGroupNameById = React.useMemo(() => {
    const m: Record<string, string> = {};
    for (const g of providerGroups) m[g.providerGroupId] = g.name;
    return m;
  }, [providerGroups]);

  const webptProviderNameById = React.useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of webptProviders) m[p.id] = p.name;
    return m;
  }, [webptProviders]);

  const renderProviderCell = (plan: ActivePlanOfCare) => {
    const groupName = plan.providerGroupId ? providerGroupNameById[plan.providerGroupId] : undefined;
    const providerNames = (plan.webptProviderIds ?? [])
      .map((id) => webptProviderNameById[id])
      .filter((n): n is string => !!n);

    if (groupName) {
      return (
        <Box>
          <Typography variant="body2" sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
            {groupName}
          </Typography>
          {providerNames.length > 0 && (
            <Tooltip title={providerNames.join(", ")} arrow>
              <Typography sx={{ fontSize: "0.75rem", color: Colors.info }}>
                {providerNames.length === 1 ? providerNames[0] : `${providerNames.length} providers`}
              </Typography>
            </Tooltip>
          )}
        </Box>
      );
    }

    if (providerNames.length > 0) {
      return (
        <Tooltip title={providerNames.length > 1 ? providerNames.join(", ") : ""} arrow disableHoverListener={providerNames.length <= 1}>
          <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
            {providerNames.length === 1 ? providerNames[0] : `${providerNames[0]} +${providerNames.length - 1}`}
          </Typography>
        </Tooltip>
      );
    }

    return (
      <Typography variant="body2" sx={{ color: Colors.info }}>
        —
      </Typography>
    );
  };

  const [activePlanOfCares, setActivePlanOfCares] = React.useState<ActivePlanOfCare[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);

  const [tab, setTab] = React.useState<"active" | "noplan">("active");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("all");
  const [sortField, setSortField] = React.useState<SortField>("lastSent");
  const [sortOrder, setSortOrder] = React.useState<SortOrder>("desc");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());

  const [filterMenuAnchor, setFilterMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [actionMenuRow, setActionMenuRow] = React.useState<ActivePlanOfCare | null>(null);
  const [bulkSending, setBulkSending] = React.useState(false);

  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const fetchActive = React.useCallback(async () => {
    if (!user?.token) return;
    setLoading(true);
    try {
      const r = await api.get("/plan-of-care/active", user.token);
      setActivePlanOfCares(r.data);
    } catch (e) {
      setSnackbar({ open: true, message: "Failed to load plans of care.", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [user?.token]);

  React.useEffect(() => {
    if (!user?.token) return;
    fetchActive();
    dispatch(getWaitlistPatients({ token: user.token }));
  }, [user?.token, fetchActive, dispatch]);

  React.useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const visiblePlans = React.useMemo(() => activePlanOfCares.filter((p) => patientMap[p.patientId]), [activePlanOfCares, patientMap]);

  const activeCount = React.useMemo(() => visiblePlans.filter((p) => !p.discharged).length, [visiblePlans]);
  const dischargedCount = visiblePlans.length - activeCount;

  const avgCompletion = React.useMemo(() => {
    const valid = visiblePlans.filter((p) => !p.discharged && p.totalAppointments && p.totalAppointments > 0).map((p) => ((p.appointmentsOccurred ?? 0) / (p.totalAppointments as number)) * 100);
    if (valid.length === 0) return undefined;
    return valid.reduce((a, b) => a + b, 0) / valid.length;
  }, [visiblePlans]);

  const onTrackCount = React.useMemo(() => visiblePlans.filter((p) => !p.discharged && !isAtRisk(p)).length, [visiblePlans]);

  const authExpiringCount = React.useMemo(() => visiblePlans.filter(isAuthExpiring).length, [visiblePlans]);

  const neverOpenedCount = React.useMemo(() => visiblePlans.filter(isNeverOpened).length, [visiblePlans]);

  const patientsWithoutPlan: OmniPatient[] = React.useMemo(() => {
    const ids = new Set(activePlanOfCares.map((p) => p.patientId));
    return patients.filter((p) => !ids.has(p.patientId));
  }, [patients, activePlanOfCares]);

  const filteredActive = React.useMemo(() => {
    let result = activePlanOfCares.filter((plan) => patientMap[plan.patientId]);

    switch (statusFilter) {
      case "active":
        result = result.filter((p) => !p.discharged);
        break;
      case "discharged":
        result = result.filter((p) => p.discharged);
        break;
      case "at-risk":
        result = result.filter(isAtRisk);
        break;
      case "auth-expiring":
        result = result.filter(isAuthExpiring);
        break;
      case "auth-exceeded":
        result = result.filter(isAuthExceeded);
        break;
      case "never-opened":
        result = result.filter(isNeverOpened);
        break;
    }

    if (debouncedSearch) {
      const q = normalize(debouncedSearch);
      result = result.filter((plan) => {
        const p = patientMap[plan.patientId];
        if (!p) return false;
        const hay = normalize(`${p.patient.firstName}${p.patient.lastName}${plan.caseStudyName ?? ""}`);
        return hay.includes(q);
      });
    }

    const cmp = (a: ActivePlanOfCare, b: ActivePlanOfCare): number => {
      const dir = sortOrder === "asc" ? 1 : -1;
      const undefLast = (av: number | undefined, bv: number | undefined) => {
        if (av === undefined && bv === undefined) return 0;
        if (av === undefined) return 1;
        if (bv === undefined) return -1;
        return (av - bv) * dir;
      };
      switch (sortField) {
        case "name": {
          const pa = patientMap[a.patientId];
          const pb = patientMap[b.patientId];
          const an = `${pa?.patient.lastName ?? ""} ${pa?.patient.firstName ?? ""}`.toLowerCase();
          const bn = `${pb?.patient.lastName ?? ""} ${pb?.patient.firstName ?? ""}`.toLowerCase();
          return an < bn ? -dir : an > bn ? dir : 0;
        }
        case "completion":
          return undefLast(actualCompletionPct(a), actualCompletionPct(b));
        case "authExpires": {
          const av = a.authorizationExpires ? DateTime.fromISO(a.authorizationExpires).toMillis() : undefined;
          const bv = b.authorizationExpires ? DateTime.fromISO(b.authorizationExpires).toMillis() : undefined;
          return undefLast(av, bv);
        }
        case "lastSent": {
          const av = a.lastSent ? DateTime.fromISO(a.lastSent).toMillis() : undefined;
          const bv = b.lastSent ? DateTime.fromISO(b.lastSent).toMillis() : undefined;
          return undefLast(av, bv);
        }
        case "lastOpened": {
          const av = a.lastOpened ? DateTime.fromISO(a.lastOpened).toMillis() : undefined;
          const bv = b.lastOpened ? DateTime.fromISO(b.lastOpened).toMillis() : undefined;
          return undefLast(av, bv);
        }
      }
    };
    result = [...result].sort(cmp);
    return result;
  }, [activePlanOfCares, patientMap, statusFilter, debouncedSearch, sortField, sortOrder]);

  const filteredNoPlan = React.useMemo(() => {
    if (!debouncedSearch) return patientsWithoutPlan;
    const q = normalize(debouncedSearch);
    return patientsWithoutPlan.filter((p) => {
      const hay = normalize(`${p.patient.firstName}${p.patient.lastName}${p.patient.phoneNumber ?? ""}`);
      return hay.includes(q);
    });
  }, [patientsWithoutPlan, debouncedSearch]);

  const visibleRows = tab === "active" ? filteredActive : filteredNoPlan;
  const paginatedActive = React.useMemo(() => filteredActive.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [filteredActive, page, rowsPerPage]);
  const paginatedNoPlan = React.useMemo(() => filteredNoPlan.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [filteredNoPlan, page, rowsPerPage]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, value: "active" | "noplan") => {
    setTab(value);
    setPage(0);
    setSelected(new Set());
    setSearchQuery("");
  };

  const handleSelectAllOnPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const next = new Set(selected);
      paginatedActive.forEach((p) => next.add(p.activePlanOfCareId));
      setSelected(next);
    } else {
      const pageIds = new Set(paginatedActive.map((p) => p.activePlanOfCareId));
      const next = new Set(Array.from(selected).filter((id) => !pageIds.has(id)));
      setSelected(next);
    }
  };

  const handleSelectOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const sendOne = async (plan: ActivePlanOfCare) => {
    if (!user?.token) throw new Error("No token");
    return api.post("/bookings/plan-of-care/send", user.token, {
      patientId: plan.patientId,
      organizationId: plan.organizationId,
      planOfCareId: plan.activePlanOfCareId,
      type: "update",
    });
  };

  const handleBulkSend = async () => {
    if (selected.size === 0) return;
    setBulkSending(true);
    const ids = Array.from(selected);
    const plans = ids.map((id) => activePlanOfCares.find((p) => p.activePlanOfCareId === id)).filter((p): p is ActivePlanOfCare => !!p);
    const results = await Promise.allSettled(plans.map((plan) => sendOne(plan)));
    const failed = results.filter((r) => r.status === "rejected").length;
    const ok = results.length - failed;
    setSnackbar({
      open: true,
      message: failed === 0 ? `Plan link sent to ${ok} patient${ok === 1 ? "" : "s"}.` : `Sent ${ok}, failed ${failed}.`,
      severity: failed === 0 ? "success" : "error",
    });
    setSelected(new Set());
    setBulkSending(false);
  };

  const handleSingleSend = async (plan: ActivePlanOfCare) => {
    setActionMenuAnchor(null);
    try {
      await sendOne(plan);
      setSnackbar({ open: true, message: "Plan link sent.", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Failed to send plan link.", severity: "error" });
    }
  };

  const activeFiltersCount = statusFilter !== "all" ? 1 : 0;

  const renderSortLabel = (field: SortField, label: string) => (
    <TableSortLabel
      active={sortField === field}
      direction={sortField === field ? sortOrder : "asc"}
      onClick={() => handleSort(field)}
      sx={{
        color: Colors.info,
        "&.Mui-active": { color: "#FFF" },
        "& .MuiTableSortLabel-icon": { color: "#FFF !important" },
      }}
    >
      {label}
    </TableSortLabel>
  );

  const summaryCards: Array<{
    label: string;
    value: React.ReactNode;
    sub: string;
    color?: string;
  }> = [
    {
      label: "Active plans",
      value: activeCount,
      sub: dischargedCount > 0 ? `${dischargedCount} discharged` : "None discharged",
    },
    {
      label: "Avg completion",
      value: avgCompletion === undefined ? "—" : `${Math.round(avgCompletion)}%`,
      sub: activeCount > 0 ? `${onTrackCount} of ${activeCount} on track` : "No active plans",
    },
    {
      label: "Auth expiring soon",
      value: authExpiringCount,
      sub: "Within 30 days",
      color: authExpiringCount > 0 ? Colors.warning : undefined,
    },
    {
      label: "Never opened",
      value: neverOpenedCount,
      sub: "Sent but unread",
      color: neverOpenedCount > 0 ? Colors.error : undefined,
    },
  ];

  const renderRowStatus = (plan: ActivePlanOfCare) => {
    const chips: React.ReactNode[] = [];
    if (plan.discharged) chips.push(statusChip("Discharged", "error"));
    if (isAtRisk(plan)) chips.push(statusChip("At risk", "warning", WarningAmberIcon));
    if (isAuthExpiring(plan)) chips.push(statusChip("Auth expiring", "warning", WarningAmberIcon));
    if (isAuthExceeded(plan)) chips.push(statusChip("Auth exceeded", "error", ErrorOutlineIcon));
    if (isNeverOpened(plan)) chips.push(statusChip("Never opened", "error", MarkEmailUnreadIcon));
    if (chips.length === 0 && !plan.discharged) chips.push(statusChip("On track", "success", CheckCircleIcon));
    return <Box sx={{ display: "flex", flexWrap: "wrap" }}>{chips}</Box>;
  };

  const relativeOrDash = (iso?: string) => {
    if (!iso) return "—";
    return DateTime.fromISO(iso).toRelative() ?? "—";
  };

  const absoluteIso = (iso?: string) => {
    if (!iso) return "";
    return DateTime.fromISO(iso).toFormat("MMM dd, yyyy h:mm a");
  };

  const skeletonRow = (cols: number, key: number) => (
    <TableRow key={`sk-${key}`}>
      {Array.from({ length: cols }).map((_, i) => (
        <TableCell key={i}>
          <Skeleton variant="text" sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }} />
        </TableCell>
      ))}
    </TableRow>
  );

  const activeColCount = 9;
  const noplanColCount = 4;

  return (
    <Box sx={{ marginTop: { xs: "75px", sm: 0 }, color: "#FFF", minHeight: "100vh", pb: 4 }}>
      <SignoutHeader />

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
          Plan of Care
        </Typography>
        <Typography variant="body1" sx={{ color: Colors.info }}>
          Track active plans of care, authorization status, and patient engagement.
        </Typography>
      </Box>

      {/* Summary cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        {summaryCards.map((card) => (
          <Paper
            key={card.label}
            sx={{
              flex: 1,
              minWidth: 220,
              p: 2.5,
              backgroundColor: "rgba(255, 255, 255, 0.02)",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: "12px",
              boxShadow: "none",
            }}
          >
            <Typography
              sx={{
                color: Colors.info,
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                mb: 1,
              }}
            >
              {card.label}
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5, color: card.color ?? "#FFF" }}>
              {loading ? <Skeleton variant="text" width={60} sx={{ bgcolor: "rgba(255,255,255,0.08)" }} /> : card.value}
            </Typography>
            <Typography sx={{ color: Colors.info, fontSize: "0.813rem" }}>{card.sub}</Typography>
          </Paper>
        ))}
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={handleTabChange}
        TabIndicatorProps={{ style: { backgroundColor: Colors.primary } }}
        sx={{
          mb: 2,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          "& .MuiTab-root": {
            color: Colors.info,
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 500,
            "&.Mui-selected": { color: "#FFF" },
          },
        }}
      >
        <Tab value="active" label={`Active Plans (${visiblePlans.length})`} />
        <Tab value="noplan" label={`No Plan Assigned (${patientsWithoutPlan.length})`} />
      </Tabs>

      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 2, flex: 1, minWidth: 300 }}>
          <TextField
            placeholder={tab === "active" ? "Search by patient name or case study..." : "Search by patient name or phone..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: Colors.info, fontSize: "18px" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              maxWidth: 400,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "rgba(255, 255, 255, 0.02)",
                color: "#FFF",
                fontSize: "0.875rem",
                "& fieldset": { borderColor: "rgba(255, 255, 255, 0.12)" },
                "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
                "&.Mui-focused fieldset": { borderColor: Colors.primary, borderWidth: "1px" },
              },
            }}
          />

          {tab === "active" && (
            <>
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  borderColor: activeFiltersCount > 0 ? Colors.primary : "rgba(255, 255, 255, 0.2)",
                  color: activeFiltersCount > 0 ? Colors.primary : "#FFF",
                  "&:hover": {
                    borderColor: Colors.primary,
                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                  },
                }}
              >
                Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
              </Button>

              <Menu
                anchorEl={filterMenuAnchor}
                open={Boolean(filterMenuAnchor)}
                onClose={() => setFilterMenuAnchor(null)}
                PaperProps={{
                  sx: { backgroundColor: Colors.bg3, color: "#FFF", minWidth: 220, mt: 1 },
                }}
              >
                <MenuItem disabled sx={{ opacity: 0.5, fontSize: "0.75rem", fontWeight: 600 }}>
                  STATUS
                </MenuItem>
                {(
                  [
                    ["all", "All Plans"],
                    ["active", "Active only"],
                    ["discharged", "Discharged"],
                    ["at-risk", "At risk"],
                    ["auth-expiring", "Auth expiring (≤30d)"],
                    ["auth-exceeded", "Auth exceeded"],
                    ["never-opened", "Never opened"],
                  ] as Array<[StatusFilter, string]>
                ).map(([value, label]) => (
                  <MenuItem
                    key={value}
                    selected={statusFilter === value}
                    onClick={() => {
                      setStatusFilter(value);
                      setPage(0);
                      setFilterMenuAnchor(null);
                    }}
                  >
                    {label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          {tab === "active" && selected.size > 0 && (
            <>
              <Typography variant="body2" sx={{ color: Colors.info, fontSize: "0.875rem" }}>
                {selected.size} selected
              </Typography>
              <Button
                variant="outlined"
                size="small"
                disabled={bulkSending}
                startIcon={bulkSending ? <CircularProgress size={14} sx={{ color: Colors.info }} /> : <SendIcon />}
                onClick={handleBulkSend}
                sx={{
                  textTransform: "none",
                  fontSize: "0.875rem",
                  borderColor: "rgba(255, 255, 255, 0.2)",
                  color: "#FFF",
                }}
              >
                Send Plan
              </Button>
              <Button variant="text" size="small" onClick={() => setSelected(new Set())} sx={{ textTransform: "none", fontSize: "0.875rem", color: Colors.info }}>
                Clear
              </Button>
            </>
          )}

          <Tooltip title="Refresh" arrow>
            <IconButton
              onClick={fetchActive}
              sx={{
                color: Colors.info,
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.08)" },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.02)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          borderRadius: "12px",
          boxShadow: "none",
        }}
      >
        {tab === "active" ? (
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
                    color: Colors.info,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    py: 2,
                  },
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={paginatedActive.length > 0 && paginatedActive.every((p) => selected.has(p.activePlanOfCareId))}
                    indeterminate={paginatedActive.some((p) => selected.has(p.activePlanOfCareId)) && !paginatedActive.every((p) => selected.has(p.activePlanOfCareId))}
                    onChange={handleSelectAllOnPage}
                    sx={{
                      color: Colors.info,
                      "&.Mui-checked": { color: Colors.primary },
                      "&.MuiCheckbox-indeterminate": { color: Colors.primary },
                    }}
                  />
                </TableCell>
                <TableCell>{renderSortLabel("name", "Patient")}</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>{renderSortLabel("completion", "Progress")}</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>{renderSortLabel("authExpires", "Authorization")}</TableCell>
                <TableCell>{renderSortLabel("lastSent", "Last Sent")}</TableCell>
                <TableCell>{renderSortLabel("lastOpened", "Last Opened")}</TableCell>
                <TableCell align="right" sx={{ position: "sticky", right: 0, zIndex: 10 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: rowsPerPage }).map((_, i) => skeletonRow(activeColCount, i))
              ) : paginatedActive.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={activeColCount} sx={{ border: 0 }}>
                    <Box sx={{ textAlign: "center", py: 8, color: Colors.info }}>
                      <AssignmentIcon sx={{ fontSize: 48, opacity: 0.2, mb: 2 }} />
                      <Typography variant="h6" sx={{ mb: 1, fontSize: "0.95rem", fontWeight: 600, color: "#FFF" }}>
                        No plans of care found
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: "0.813rem" }}>
                        {debouncedSearch || activeFiltersCount > 0 ? "Try adjusting your search or filters" : "Plans assigned to patients will appear here"}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedActive.map((plan) => {
                  const patient = patientMap[plan.patientId];
                  if (!patient) return null;
                  const completion = actualCompletionPct(plan);
                  const isSelected = selected.has(plan.activePlanOfCareId);
                  return (
                    <TableRow
                      key={plan.activePlanOfCareId}
                      hover
                      selected={isSelected}
                      sx={{
                        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.04) !important" },
                        "& td": {
                          borderBottom: "1px solid rgba(255, 255, 255, 0.04)",
                          color: "#FFF",
                          fontSize: "0.875rem",
                          py: 2,
                        },
                        "&.Mui-selected": { backgroundColor: "rgba(25, 118, 210, 0.08)" },
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={() => handleSelectOne(plan.activePlanOfCareId)}
                          sx={{
                            color: Colors.info,
                            "&.Mui-checked": { color: Colors.primary },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                            {patient.patient.firstName} {patient.patient.lastName}
                          </Typography>
                          {patient.waitlist?.archived && (
                            <Chip
                              label="Archived"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.688rem",
                                fontWeight: 500,
                                backgroundColor: "rgba(255, 152, 0, 0.12)",
                                color: "#FF9800",
                                border: "1px solid rgba(255, 152, 0, 0.3)",
                                "& .MuiChip-label": { px: 1 },
                              }}
                            />
                          )}
                        </Box>
                        {plan.caseStudyName && (
                          <Typography variant="caption" sx={{ color: Colors.info, fontSize: "0.75rem" }}>
                            {plan.caseStudyName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{renderProviderCell(plan)}</TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: 140 }}>
                          {completion === undefined ? (
                            <Typography variant="body2" sx={{ color: Colors.info, mb: 0.25 }}>
                              —
                            </Typography>
                          ) : (
                            <>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(100, Math.max(0, completion))}
                                  sx={{
                                    flex: 1,
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: "rgba(255,255,255,0.08)",
                                    "& .MuiLinearProgress-bar": {
                                      backgroundColor: isAtRisk(plan) ? Colors.warning : Colors.success,
                                      borderRadius: 3,
                                    },
                                  }}
                                />
                                <Typography sx={{ fontSize: "0.75rem", color: "#FFF", minWidth: 32 }}>{Math.round(completion)}%</Typography>
                              </Box>
                              <Typography sx={{ fontSize: "0.75rem", color: Colors.info }}>
                                {plan.appointmentsOccurred ?? 0}/{plan.totalAppointments} visits
                              </Typography>
                            </>
                          )}
                          {(() => {
                            const aggregatesPresent = plan.totalAppointments !== undefined;
                            const calcSource = plan.lastManualEdit ?? plan.createdAt;
                            const tooltipText = aggregatesPresent ? absoluteIso(calcSource) || "Never calculated" : "Aggregates not yet computed. Open this plan and save to refresh.";
                            const labelText = aggregatesPresent ? `Calculated ${relativeOrDash(calcSource)}` : "Never calculated";
                            return (
                              <Tooltip title={tooltipText} arrow>
                                <Typography
                                  sx={{
                                    fontSize: "0.688rem",
                                    color: aggregatesPresent ? Colors.info : Colors.warning,
                                    fontStyle: "italic",
                                    mt: 0.25,
                                  }}
                                >
                                  {labelText}
                                </Typography>
                              </Tooltip>
                            );
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell>{renderRowStatus(plan)}</TableCell>
                      <TableCell>
                        {plan.ignoreAuthorization ? (
                          statusChip("Auth ignored", "info")
                        ) : plan.authorizationTotal === undefined && plan.authorizationUsed === undefined ? (
                          <Typography variant="body2" sx={{ color: Colors.info }}>
                            —
                          </Typography>
                        ) : (
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                              {plan.authorizationUsed ?? 0}/{plan.authorizationTotal ?? "?"}
                            </Typography>
                            {plan.authorizationExpires && (
                              <Tooltip title={absoluteIso(plan.authorizationExpires)} arrow>
                                <Typography sx={{ fontSize: "0.75rem", color: Colors.info }}>Expires {relativeOrDash(plan.authorizationExpires)}</Typography>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={absoluteIso(plan.lastSent)} arrow disableHoverListener={!plan.lastSent}>
                          <Typography variant="body2" sx={{ fontSize: "0.875rem", color: plan.lastSent ? "#FFF" : Colors.info }}>
                            {relativeOrDash(plan.lastSent)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={absoluteIso(plan.lastOpened)} arrow disableHoverListener={!plan.lastOpened}>
                          <Typography variant="body2" sx={{ fontSize: "0.875rem", color: plan.lastOpened ? "#FFF" : Colors.info }}>
                            {relativeOrDash(plan.lastOpened)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ position: "sticky", right: 0, zIndex: 10 }}>
                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                          <Tooltip title="View plan" arrow>
                            <IconButton
                              size="small"
                              component={Link}
                              to={`/dashboard/patients/${plan.patientId}/plan-of-care/${plan.activePlanOfCareId}`}
                              sx={{
                                color: Colors.info,
                                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.08)" },
                              }}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              setActionMenuAnchor(e.currentTarget);
                              setActionMenuRow(plan);
                            }}
                            sx={{
                              color: Colors.info,
                              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.08)" },
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        ) : (
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
                    color: Colors.info,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    py: 2,
                  },
                }}
              >
                <TableCell>Patient</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Added On</TableCell>
                <TableCell align="right" sx={{ position: "sticky", right: 0, zIndex: 10 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: rowsPerPage }).map((_, i) => skeletonRow(noplanColCount, i))
              ) : paginatedNoPlan.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={noplanColCount} sx={{ border: 0 }}>
                    <Box sx={{ textAlign: "center", py: 8, color: Colors.info }}>
                      <AssignmentIcon sx={{ fontSize: 48, opacity: 0.2, mb: 2 }} />
                      <Typography variant="h6" sx={{ mb: 1, fontSize: "0.95rem", fontWeight: 600, color: "#FFF" }}>
                        All patients have an active plan of care.
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: "0.813rem" }}>
                        Patients without a plan will appear here so you can assign one.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedNoPlan.map((p) => (
                  <TableRow
                    key={p.patientId}
                    hover
                    sx={{
                      "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.04) !important" },
                      "& td": {
                        borderBottom: "1px solid rgba(255, 255, 255, 0.04)",
                        color: "#FFF",
                        fontSize: "0.875rem",
                        py: 2,
                      },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                        {p.patient.firstName} {p.patient.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "0.875rem", color: Colors.info }}>
                        {convertE164ToReadable(p.patient.phoneNumber)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "0.875rem", color: Colors.info }}>
                        {relativeOrDash(p.patient.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ position: "sticky", right: 0, zIndex: 10 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        component={Link}
                        to={`/dashboard/patients/${p.patientId}/plan-of-care`}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.813rem",
                          fontWeight: 600,
                          boxShadow: "none",
                          "&:hover": { boxShadow: "none" },
                        }}
                      >
                        Create Plan of Care
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}

        <TablePagination
          component="div"
          count={visibleRows.length}
          page={page}
          onPageChange={(_, p) => {
            setPage(p);
            setSelected(new Set());
          }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
            setSelected(new Set());
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            color: "#FFF",
            borderTop: "1px solid rgba(255, 255, 255, 0.08)",
            "& .MuiTablePagination-select": { color: "#FFF" },
            "& .MuiTablePagination-selectIcon": { color: "#FFF" },
            "& .MuiTablePagination-displayedRows": { color: Colors.info },
            "& .MuiIconButton-root": { color: "#FFF" },
          }}
        />
      </TableContainer>

      {/* Row action menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => {
          setActionMenuAnchor(null);
          setActionMenuRow(null);
        }}
        PaperProps={{
          sx: { backgroundColor: Colors.bg3, color: "#FFF", minWidth: 180, mt: 1 },
        }}
      >
        <MenuItem
          onClick={() => {
            if (actionMenuRow) handleSingleSend(actionMenuRow);
          }}
        >
          <SendIcon sx={{ fontSize: 16, mr: 1, color: Colors.info }} />
          Send Plan
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionMenuRow) {
              navigate(`/dashboard/patients/${actionMenuRow.patientId}/plan-of-care/${actionMenuRow.activePlanOfCareId}`);
            }
            setActionMenuAnchor(null);
            setActionMenuRow(null);
          }}
        >
          <OpenInNewIcon sx={{ fontSize: 16, mr: 1, color: Colors.info }} />
          View Details
        </MenuItem>
      </Menu>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            backgroundColor: snackbar.severity === "success" ? "#4CAF50" : Colors.error,
            color: "#FFF",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
